
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  auditShopifyAccessTokenCopy,
  ShopifyAccessTokenReveal,
} from '@/models/shopifyAccessToken';

@Component
export default class ShopifyAccessTokenDialog extends Vue {
  @Prop({ required: true }) public value!: boolean;
  @Prop({ required: true }) public tokenData!: ShopifyAccessTokenReveal;

  public isVisible: boolean = false;
  public remaining: string = '';
  public isExpired: boolean = false;
  public copyMessage: string = '';
  public copyError: boolean = false;
  private countdownTimer: number | null = null;

  public get formattedExpiry(): string {
    if (!this.tokenData.expiresAt) {
      return 'Legacy token — no expiration recorded';
    }
    const expiry = new Date(this.tokenData.expiresAt);
    return Number.isFinite(expiry.getTime()) ? expiry.toLocaleString() : 'Expiration unavailable';
  }

  @Watch('value', { immediate: true })
  public onValueChanged(open: boolean) {
    if (open) {
      this.startCountdown();
    } else {
      this.cleanup();
    }
  }

  public onDialogInput(open: boolean) {
    if (!open) {
      this.close();
    }
  }

  public startCountdown() {
    this.stopCountdown();
    this.updateRemaining();
    if (!this.isExpired) {
      this.countdownTimer = window.setInterval(() => this.updateRemaining(), 1000);
    }
  }

  public updateRemaining() {
    if (!this.tokenData.expiresAt) {
      this.remaining = 'Does not expire (legacy token)';
      this.isExpired = false;
      return;
    }
    const expiresAt = new Date(this.tokenData.expiresAt).getTime();
    if (!Number.isFinite(expiresAt)) {
      this.remaining = 'Expiration unavailable';
      this.isExpired = true;
      this.stopCountdown();
      return;
    }
    const milliseconds = expiresAt - Date.now();
    if (milliseconds <= 0) {
      this.remaining = 'Expired';
      this.isExpired = true;
      this.stopCountdown();
      return;
    }
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    this.remaining = [hours ? `${hours}h` : '', `${minutes}m`, `${seconds}s`].filter(Boolean).join(' ');
    this.isExpired = false;
  }

  public async copyToken() {
    this.copyMessage = '';
    this.copyError = false;
    try {
      await navigator.clipboard.writeText(this.tokenData.accessToken);
      await auditShopifyAccessTokenCopy(this.tokenData.storeId, this.tokenData.retrievedAt);
      this.copyMessage = 'Copied';
    } catch (error) {
      this.copyError = true;
      this.copyMessage = 'Unable to copy or audit the copy action.';
    }
  }

  public close() {
    this.cleanup();
    this.$emit('clear');
    this.$emit('input', false);
  }

  public stopCountdown() {
    if (this.countdownTimer !== null) {
      window.clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  public cleanup() {
    this.stopCountdown();
    this.isVisible = false;
    this.copyMessage = '';
    this.copyError = false;
  }

  public beforeDestroy() {
    this.cleanup();
  }
}
